import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SimpleArrowAccordionV2 } from 'components/Accordion/SimpleArrowAccordionV2/SimpleArrowAccordionV2';
import type { IAccordionItem } from 'components/Accordion/models';
import { useValidatedProgramPromotion } from 'hooks/ProgramPromotion/useValidatedProgramPromotion';
import { AppliedPromo } from './AppliedPromo/AppliedPromo';
import { LoadingPromo } from './LoadingPromo/LoadingPromo';
import { PromoCodeInput } from './PromoCodeInput/PromoCodeInput';

import styles from './PromoSummary.module.scss';
import { PROMO_SUMMARY_TEST_ID } from './PromoSummary.const';
import { useFetchingPromoInternal } from './FetchingPromoContext/useFetchingPromoInternal';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

interface IPromoSummaryProps {
  promoBoxVisible?: boolean;
}

export const PromoSummary: React.FC<IPromoSummaryProps> = ({ promoBoxVisible = true }) => {
  const {
    addPromo,
    checkout: { promo: promoInCart },
  } = useCartContext();
  const { programPromotion, isProgramPromoLoading } = useValidatedProgramPromotion();
  const { t } = useTranslation();
  const { fetchingPromo } = useFetchingPromoInternal();
  const [accordionState, setAccordionState] = useState(true);

  const promotionLoading = fetchingPromo.status === 'loading' || isProgramPromoLoading;
  const isActivePromoApplicableToCartItems = !!(fetchingPromo.data || programPromotion);
  const isPromoApplied = !promotionLoading && promoInCart.applied && isActivePromoApplicableToCartItems;

  useEffect(() => {
    if (!programPromotion) return;

    addPromo({
      program: programPromotion.promoCode,
    });
    setAccordionState(false);
  }, [programPromotion]);

  useEffect(() => {
    if (fetchingPromo.status !== 'success') return;

    addPromo({
      applied: fetchingPromo.data.promoCode,
    });
    setAccordionState(false);
  }, [fetchingPromo]);

  useEffect(() => {
    if (promoInCart.program !== null) {
      setAccordionState(false);
    }
  }, [promoInCart.program]);

  useEffect(() => {
    setAccordionState(true);
  }, [isActivePromoApplicableToCartItems]);

  return (
    <section className={styles.promoSummary} aria-atomic="true" role="status">
      {promoBoxVisible && (
        <SimpleArrowAccordionV2
          className={styles.promoSummaryAccordion}
          items={[
            {
              id: 0,
              defaultOpen: accordionState,
              itemContainerClassName: styles.promoSummaryAccordionBody,
              headerClassName: styles.promoSummaryAccordionHeader,
              title: t('promo-code.heading'),
              body: (
                <PromoCodeInput
                  handleProgramPromo={() => setAccordionState(false)}
                  isActivePromoApplicableToCartItems={isActivePromoApplicableToCartItems}
                />
              ),
            } as IAccordionItem,
          ]}
          preventClickHandler
          data-testid={PROMO_SUMMARY_TEST_ID.accordian}
        />
      )}
      <div aria-live="assertive" role="status">
        {promotionLoading && <LoadingPromo />}
        {isPromoApplied && (
          <AppliedPromo
            handleRemovePromo={() => setAccordionState(true)}
            disabledXButton={!promoBoxVisible}
          />
        )}
      </div>
    </section>
  );
};

PromoSummary.displayName = 'PromoSummary';
